'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { cinzel } from '@/lib/fonts';

const posts = [1, 2, 3, 4, 5, 6].map((n) => ({
    video: `/videos/our-work/koza/reel-${n}.mp4`,
    poster: `/images/our-work/koza-smm-post${n}.webp`,
    label: `KOZA Instagram reel ${n}`,
}));

export default function KozaSMMPage() {
    const postRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        document.title = 'KOZA — Social Media | Lead Workz';
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('koza-smm-post-in');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.1 }
        );
        postRefs.current.forEach((el) => { if (el) observer.observe(el); });
        return () => observer.disconnect();
    }, []);

    return (
        <div className={cinzel.variable}>
            <style>{`
                @keyframes koza-smm-rise {
                    from { opacity: 0; transform: translateY(48px) scale(0.97); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                .koza-smm-post { opacity: 0; }
                .koza-smm-post.koza-smm-post-in {
                    animation: koza-smm-rise 0.75s cubic-bezier(0.22, 1, 0.36, 1) both;
                }
                .koza-smm-post:nth-child(1).koza-smm-post-in { animation-delay: 0.05s; }
                .koza-smm-post:nth-child(2).koza-smm-post-in { animation-delay: 0.18s; }
                .koza-smm-post:nth-child(3).koza-smm-post-in { animation-delay: 0.31s; }
                .koza-smm-post:nth-child(4).koza-smm-post-in { animation-delay: 0.44s; }
                .koza-smm-post:nth-child(5).koza-smm-post-in { animation-delay: 0.57s; }
                .koza-smm-post:nth-child(6).koza-smm-post-in { animation-delay: 0.70s; }
                .koza-smm-post {
                    transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1),
                                box-shadow 0.4s cubic-bezier(0.22, 1, 0.36, 1);
                }
                .koza-smm-post:hover {
                    transform: translateY(-10px) scale(1.02);
                    box-shadow: 0 24px 56px rgba(12, 22, 49, 0.2);
                    z-index: 2;
                }
                @media (max-width: 768px) {
                    .koza-smm-card { max-width: calc(100% - 32px) !important; border-radius: 40px !important; }
                    .koza-smm-hero { padding: 48px 24px 40px !important; }
                    .koza-smm-grid { grid-template-columns: 1fr 1fr !important; gap: 12px !important; }
                    .koza-smm-grid-section { padding: 40px 20px !important; }
                    .koza-smm-follow-row { flex-direction: column !important; align-items: center !important; text-align: center !important; }
                    .koza-smm-follow-info { align-items: center !important; }
                }
            `}</style>

            <main style={{ background: '#080400', minHeight: '100vh', paddingBottom: 80 }}>
                <div style={{ height: 112 }} />

                {/* ── White rounded card ── */}
                <div className="koza-smm-card" style={{
                    background: '#fbfdfd',
                    borderRadius: 100,
                    width: 1798,
                    maxWidth: 'calc(100% - 40px)',
                    margin: '0 auto',
                    overflow: 'hidden',
                }}>

                    {/* ── Brand Logo section ── */}
                    <div className="koza-smm-hero" style={{
                        padding: 'clamp(48px, 7vw, 100px) clamp(32px, 6vw, 102px)',
                        boxSizing: 'border-box',
                    }}>
                        <div style={{ maxWidth: 1643, margin: '0 auto' }}>
                            <h1 style={{
                                fontFamily: 'var(--font-cinzel), Cinzel, serif',
                                fontWeight: 700,
                                fontSize: 'clamp(28px, 4.5vw, 70px)',
                                letterSpacing: '0.13em',
                                color: '#0c1631',
                                margin: '0 0 24px',
                                lineHeight: 'normal',
                            }}>
                                SOCIAL MEDIA STRATEGY
                            </h1>
                            <div style={{
                                fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                fontWeight: 500,
                                fontSize: 'clamp(14px, 1.4vw, 22px)',
                                color: '#080400',
                                letterSpacing: '0.08em',
                                lineHeight: 1.8,
                            }}>
                                <p style={{ margin: '0 0 14px' }}>For KOZA, our focus extended beyond creating visually appealing content. We developed a social media strategy designed to strengthen the brand's digital presence, increase audience engagement, and support long-term organic growth.</p>
                                <p style={{ margin: '0 0 14px' }}>Our team created platform-specific content that reflected KOZA's premium skincare positioning while maintaining a consistent visual identity across every touchpoint. Each post was planned around audience interests, seasonal trends, and content pillars to encourage meaningful interactions rather than vanity metrics.</p>
                                <p style={{ margin: '0 0 14px' }}>To improve discoverability, we integrated SEO principles into the content strategy through keyword-driven captions, relevant hashtags, optimized profile elements, and content aligned with what potential customers actively search for online. This helped strengthen KOZA's visibility across both social platforms and search engines while reinforcing its overall digital presence.</p>
                                <p style={{ margin: 0 }}>Alongside content creation, we monitored engagement patterns, refined posting strategies based on performance data, and continuously optimized campaigns to maximize reach, build brand credibility, and foster a loyal online community. The result was a cohesive social media presence that not only enhanced brand perception but also contributed to sustainable organic growth.</p>
                            </div>
                        </div>
                    </div>

                    {/* ── 6-post Instagram grid ── */}
                    <div className="koza-smm-grid-section" style={{
                        padding: 'clamp(24px, 3vw, 56px) clamp(24px, 5vw, 87px)',
                        boxSizing: 'border-box',
                    }}>
                        <div className="koza-smm-grid" style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 'clamp(8px, 1.2vw, 16px)',
                            maxWidth: 1020,
                            margin: '0 auto',
                        }}>
                            {posts.map((post, i) => (
                                <div
                                    key={i}
                                    ref={(el) => { postRefs.current[i] = el; }}
                                    className="koza-smm-post"
                                    style={{
                                        aspectRatio: '9 / 16',
                                        borderRadius: 'clamp(6px, 0.8vw, 12px)',
                                        overflow: 'hidden',
                                        background: '#e8e5df',
                                        position: 'relative',
                                    }}
                                >
                                    <video
                                        src={post.video}
                                        poster={post.poster}
                                        aria-label={post.label}
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        preload="metadata"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            objectPosition: 'top center',
                                            display: 'block',
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Dark footer: Follow on Instagram ── */}
                    <div style={{
                        background: '#0c1631',
                        borderRadius: '0 0 100px 100px',
                        padding: 'clamp(48px, 5vw, 80px) clamp(24px, 5vw, 87px) clamp(60px, 6vw, 100px)',
                        boxSizing: 'border-box',
                    }}>
                        {/* Heading */}
                        <h2 style={{
                            fontFamily: 'var(--font-cinzel), Cinzel, serif',
                            fontWeight: 400,
                            fontSize: 'clamp(22px, 3vw, 50px)',
                            letterSpacing: '0.05em',
                            color: '#f5fde9',
                            margin: '0 0 clamp(32px, 4vw, 60px)',
                            textTransform: 'capitalize',
                            textAlign: 'center',
                        }}>
                            Follow Us On{' '}
                            <span style={{
                                fontStyle: 'italic',
                                fontSize: 'clamp(28px, 4.5vw, 80px)',
                            }}>
                                I
                            </span>
                            nstagram
                        </h2>

                        {/* Profile row */}
                        <div className="koza-smm-follow-row" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'clamp(24px, 5vw, 88px)',
                            maxWidth: 1348,
                            margin: '0 auto',
                        }}>
                            {/* Avatar circle */}
                            <div style={{
                                width: 'clamp(100px, 17vw, 333px)',
                                height: 'clamp(100px, 17vw, 333px)',
                                flexShrink: 0,
                                position: 'relative',
                                borderRadius: '50%',
                                background: '#f5fde9',
                                border: '3px solid #e0c58f',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src="/images/our-work/koza-smm-logo.webp"
                                    alt="KOZA Aesthetic Care logo"
                                    style={{
                                        width: '66%',
                                        height: 'auto',
                                        objectFit: 'contain',
                                    }}
                                />
                            </div>

                            {/* Info */}
                            <div className="koza-smm-follow-info" style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'clamp(16px, 2vw, 32px)',
                                alignItems: 'flex-start',
                            }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    <p style={{
                                        fontFamily: '"Inter", sans-serif',
                                        fontWeight: 400,
                                        fontSize: 'clamp(18px, 2.5vw, 40px)',
                                        color: '#f5fde9',
                                        margin: 0,
                                        textTransform: 'lowercase',
                                    }}>
                                        @koza.aesthetic.care
                                    </p>
                                    <p style={{
                                        fontFamily: '"Inter", sans-serif',
                                        fontWeight: 300,
                                        fontSize: 'clamp(13px, 1.6vw, 32px)',
                                        color: '#d28734',
                                        margin: 0,
                                        textTransform: 'uppercase',
                                    }}>
                                        ADVANCED SKIN | HAIR | SLIMMING | COSMETIC PROCEDURES
                                    </p>
                                </div>

                                {/* Follow button */}
                                <Link
                                    href="https://www.instagram.com/koza.aesthetic.care"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: '#f5fde9',
                                        color: '#d28a36',
                                        fontFamily: '"Inter", sans-serif',
                                        fontWeight: 400,
                                        fontSize: 'clamp(14px, 1.3vw, 24px)',
                                        letterSpacing: '0.05em',
                                        textTransform: 'uppercase',
                                        padding: 'clamp(12px, 1.5vw, 22px) clamp(24px, 3vw, 56px)',
                                        borderRadius: 50,
                                        textDecoration: 'none',
                                        width: 'fit-content',
                                        transition: 'opacity 0.2s',
                                    }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = '0.85'; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = '1'; }}
                                >
                                    Follow
                                </Link>
                            </div>
                        </div>
                    </div>

                </div>

                {/* ── Back link ── */}
                <div style={{ textAlign: 'center', marginTop: 60 }}>
                    <Link
                        href="/our-work/social-media-marketing"
                        style={{
                            fontFamily: 'var(--font-manrope)',
                            fontWeight: 500,
                            fontSize: 16,
                            color: '#fbfdfd',
                            opacity: 0.5,
                            textDecoration: 'none',
                            letterSpacing: '0.05em',
                            transition: 'opacity 0.2s',
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = '1'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = '0.5'; }}
                    >
                        ← Back to Social Media Marketing
                    </Link>
                </div>
            </main>
        </div>
    );
}
